import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

import { tap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AccountSetting } from '@app/shared/models';
import { AuthService } from '@app/shared/auth/auth.service';
import { AccountAccessGuardService } from '../helpers/account-access-guard.service';
import { DEFAULT_TOOTH_NUMBERING_SYSTEM, TOOTH_NUMBERING_SYSTEM_KEY } from '@app/shared/constants/settings.constants';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  apiUrlV2 = environment.apiUrlV2+ '/accounts/account-settings';

  settings: AccountSetting[];
  accountSettings: AccountSetting[] = [];
  associateSettings: AccountSetting[] = [];
  private _toothNumberingPref: string | null = null;

  constructor(private http: HttpClient, private authSvc: AuthService, private accountAccessGuardService: AccountAccessGuardService) {
    // this.getInitRecords({}).subscribe();
  }

  getInitRecords(params: { associate_id?: number; set_key?: string } = {}) {
    return this.http.get(`${this.apiUrlV2}`, { params: params as any }).pipe(
      tap((response: any) => {
        const data = <AccountSetting[]>response.data;
        this.settings = data;
        if (params.associate_id) {
          this.accountSettings = data.filter((r) => (r.associateId ?? 0) === 0);
          this.associateSettings = data.filter((r) => (r.associateId ?? 0) > 0);
        } else {
          this.accountSettings = data;
          this.associateSettings = [];
        }
        this._toothNumberingPref = null;
      }),
      catchError(() => of(null))
    );
  }

  getSettings(associate?: boolean): Observable<any> {
    if (associate) {
      const userId = this.authSvc.user?.data?.id;
      if (!userId) return of([]);
      if (this.associateSettings?.length || this.accountSettings?.length) {
        return of(this.associateSettings.length ? this.associateSettings : this.accountSettings);
      }
      return this.getInitRecords({ associate_id: userId });
    } else {
      if (this.accountSettings?.length) {
        return of(this.accountSettings);
      }
      return this.getInitRecords({});
    }
  }

  getSetting(associate?: boolean, key?: string): Observable<any> {
    const userId = this.authSvc.user?.data?.id;
    if (associate && userId) {
      return this.getInitRecords({ associate_id: userId, set_key: key });
    }
    return of(null);
  }

  getSettingRow(key: string): AccountSetting | undefined {
    const assoc = this.associateSettings?.find((r) => r.setKey === key);
    if (assoc) {
      return assoc;
    }
    const arr = this.accountSettings?.length ? this.accountSettings : this.settings;
    return arr?.find((r) => r.setKey === key);
  }

  patchSettingValue(key: string, value: string): void {
    const row = this.getSettingRow(key);
    if (row) {
      row.setValue = value;
    }
  }

  get(key: string) {
    if (key === TOOTH_NUMBERING_SYSTEM_KEY) {
      return this.getToothNumberingPreference();
    }
    const assoc = this.associateSettings?.find((r) => r.setKey === key);
    if (assoc?.setValue != null && assoc.setValue !== '') {
      return assoc.setValue;
    }
    const row = this.getSettingRow(key);
    if (!row) {
      return null;
    }
    return row.setValue === '' ? null : row.setValue;
  }

  /**
   * Resolves tooth numbering when both account (clinic) and per-user rows exist:
   * use whichever was updated most recently so a new clinic default beats a stale per-user row.
   * Equal timestamps: per-user row wins (e.g. same-second updates).
   * Both updatedDt missing/invalid: prefer account (clinic default) over a stale per-user value.
   */
  getToothNumberingPreference(): string {
    if (this._toothNumberingPref !== null) return this._toothNumberingPref;
    const key = TOOTH_NUMBERING_SYSTEM_KEY;
    const assoc = this.associateSettings?.find((r) => r.setKey === key);
    const acct = this.accountSettings?.find((r) => r.setKey === key);
    const assocVal = assoc?.setValue;
    const acctVal = acct?.setValue;
    const assocHas = assocVal != null && assocVal !== '';
    const acctHas = acctVal != null && acctVal !== '';

    if (assocHas && acctHas) {
      const assocMs = this.settingUpdatedMs(assoc);
      const acctMs = this.settingUpdatedMs(acct);
      if (assocMs > acctMs) {
        this._toothNumberingPref = assocVal;
        return assocVal;
      }
      if (acctMs > assocMs) {
        this._toothNumberingPref = acctVal;
        return acctVal;
      }
      if (assocMs === 0 && acctMs === 0) {
        this._toothNumberingPref = acctVal;
        return acctVal;
      }
      this._toothNumberingPref = assocVal;
      return assocVal;
    }
    if (assocHas) {
      this._toothNumberingPref = assocVal;
      return assocVal;
    }
    if (acctHas) {
      this._toothNumberingPref = acctVal;
      return acctVal;
    }
    const fallback = this.settings?.find((r) => r.setKey === key);
    this._toothNumberingPref = fallback?.setValue || DEFAULT_TOOTH_NUMBERING_SYSTEM;
    return this._toothNumberingPref;
  }

  private settingUpdatedMs(row: AccountSetting | undefined): number {
    if (!row?.updatedDt) {
      return 0;
    }
    const t = Date.parse(row.updatedDt);
    return Number.isNaN(t) ? 0 : t;
  }

  getKiosk() {
    return this.getInitRecords({});
  }

  save(data: any[], associate?: boolean) {
    const userId = this.authSvc.user?.data?.id;
    let set_str = JSON.stringify(data);
    set_str = set_str.replace(/\\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
    const settings: any = { settings: set_str, account_id: this.authSvc.getUserData('account_id') };
    if (associate && userId) {
      settings.associate_id = userId;
    }
    return this.accountAccessGuardService.accountGuardRestriction(() => this.http.post(`${this.apiUrlV2}`, settings).pipe(
      tap((res: any) => {
        const params = associate && userId ? { associate_id: userId } : {};
        this.getInitRecords(params).subscribe();
      })
    ));
  }
}
